import React, { useEffect, useState, useCallback, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { X, Share2, ExternalLink, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { WatchButton } from '@/components/ui/watch-button';
import { EntityToolbarButtons, type ToolbarAction } from '@/components/ui/entity-toolbar-buttons';
import { useFloatingBallFootprint } from '@/hooks/useFloatingBallFootprint';

// Shared across Flyout and FlyoutHeader so a new entity type only needs one edit.
// `route` only         → path-style URL  (/route/{id})
// `route` + `idParam`  → query-string URL (/route?{idParam}={id})
type EntityRoute = { route: string; idParam?: string };

const ENTITY_ROUTE_MAP: Record<string, EntityRoute> = {
  task: { route: '/tasks' },
  // 统一 Intent 页是唯一分诊面（Intake console 已并入）——share/open-in-page
  // 落在列表页并展开该 Intent 的 flyout。
  intent: { route: '/intents', idParam: 'intentId' },
  reservation: { route: '/reservations' },
  review: { route: '/reviews' },
  property: { route: '/properties' },
  guest: { route: '/guests' },
  owner: { route: '/owners' },
  'staff-attendance': { route: '/staff-attendance' },
  // KB 有独立详情页（正文 + 详情侧栏）；列表页的 ?unit= flyout 深链仍兼容旧链接。
  knowledge_unit: { route: '/knowledge-base' },
  // procurement purchase detail lives in a flyout on the list page (query-param deep link)
  purchase: { route: '/procurement', idParam: 'purchaseId' },
  procurement_purchase: { route: '/procurement', idParam: 'purchaseId' },
  // asset-mgr (query-param style — no dedicated detail route)
  inventory_balance: { route: '/asset-mgr/inventory', idParam: 'balanceId' },
  product: { route: '/asset-mgr/products', idParam: 'productId' },
  inventory_transaction: { route: '/asset-mgr/transactions', idParam: 'transactionId' },
};

// 打开中的 Flyout,按打开先后排。Esc 只交给栈顶那个:叠开两层时(详情里点预订号叠一层
// ReservationFlyout),每层各自在 window 上挂 keydown,一次 Esc 会把两层一起关掉。
// 用 token 对象而不是 useId:同一实例关了又开要重新排到栈顶。
const openFlyoutStack: object[] = [];

/**
 * 当前是否有打开中的 Flyout。给页面级 Esc 处理用(如 ListHeader 的 Esc 清搜索):
 * 那些监听挂在 document 上、比这里挂在 window 上的更早执行,又看不到 defaultPrevented,
 * 不问一句就会把「关抽屉的 Esc」也当成「清搜索」。
 */
export function hasOpenFlyout(): boolean {
  return openFlyoutStack.length > 0;
}

// ============================================
// Flyout Component
// ============================================

// 面板的层下限（FlyoutLayerProvider）在 stacking-layer：测试常 mock 这个文件，层的规则不能跟着被 mock 掉
export { FlyoutLayerProvider, useFlyoutLayer } from '@/components/ui/stacking-layer'
import { liftedTo, useFlyoutLayer } from '@/components/ui/stacking-layer'

interface FlyoutProps {
  // New unified API (preferred)
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  // Legacy API (for backward compatibility)
  /** @deprecated Use `open` instead */
  isOpen?: boolean;
  /** @deprecated Use `onOpenChange` instead */
  onClose?: () => void;

  // Header configuration
  title?: React.ReactNode;
  showHeader?: boolean; // Whether to show the built-in header (default: true when title is provided)

  // Content
  children: React.ReactNode;

  // Layout
  side?: 'left' | 'right';
  actions?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full' | 'custom';
  width?: string | number; // Custom width when size='custom'
  noPadding?: boolean;

  // Actions
  onOpenInNewTab?: () => void;
  onShare?: () => void;

  // Watch feature
  entityType?: string;
  entityId?: string;
  otherWatchersCount?: number;

  // Mode
  readonly?: boolean; // 只读模式，隐藏 Watch 按钮等交互功能

  // Visual
  className?: string;
  showBackdrop?: boolean; // Whether to show the backdrop overlay (default: true)
  usePortal?: boolean; // Whether to render using Portal (default: false)

  /**
   * Embedded mode - for Detail pages
   * When true:
   * - Renders content directly without fixed positioning
   * - No backdrop
   * - No animations
   * - Ignores open/isOpen state (always visible)
   */
  embedded?: boolean;
}

export const Flyout: React.FC<FlyoutProps> = ({
  // New API
  open,
  onOpenChange,
  // Legacy API
  isOpen: legacyIsOpen,
  onClose: legacyOnClose,
  // Header
  title,
  showHeader,
  // Content
  children,
  // Layout
  side = 'right',
  actions,
  size = 'xl',
  width,
  noPadding = false,
  // Actions
  onOpenInNewTab,
  onShare,
  // Watch
  entityType,
  entityId,
  otherWatchersCount = 0,
  // Mode
  readonly = false,
  // Visual
  className,
  showBackdrop = true,
  usePortal = false,
  embedded = false
}) => {
  const { t } = useTranslation('common')
  // Resolve the actual open state (new API takes precedence)
  const isOpen = open ?? legacyIsOpen ?? false;

  // Use refs to keep stable close handler (avoids useEffect re-runs from inline callbacks)
  const onOpenChangeRef = useRef(onOpenChange);
  const legacyOnCloseRef = useRef(legacyOnClose);
  onOpenChangeRef.current = onOpenChange;
  legacyOnCloseRef.current = legacyOnClose;

  const handleClose = useCallback(() => {
    if (onOpenChangeRef.current) {
      onOpenChangeRef.current(false);
    } else if (legacyOnCloseRef.current) {
      legacyOnCloseRef.current();
    }
  }, []);

  // Determine if header should be shown
  const shouldShowHeader = showHeader ?? (title !== undefined);
  // 每个实例一个唯一 id。曾经写死成 "flyout-title" —— 页面上常常同时挂着多个
  // Flyout(一个详情面板已挂载但 invisible,另一个被打开),id 一撞,浏览器只解析
  // DOM 里的第一个,于是打开的那个面板在无障碍树上顶着别人的名字(NOVAO-61:
  // 点 Manage Departments,屏幕阅读器念的是 User Profile)。视觉正常,所以肉眼
  // 看不出来;Playwright 的 getByRole('dialog', {name}) 也会因此命中错元素。
  const titleId = `flyout-title-${useId()}`;
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);
  // 整页详情(embedded)底部「主按钮 + ⋯」操作栏的 ⋯ 在最右端，右下角电话悬浮球的
  // 默认落点正好压住它。embedded 时把球占掉的宽度写进 --floating-ball-clearance，
  // 操作栏的右内边距读它；浮层形态是 z-50、本来就盖在球(z-40)上，重置回 1rem，
  // 免得嵌在整页详情里的浮层也跟着让位。
  const floatingBallFootprint = useFloatingBallFootprint();
  const flyoutLayer = useFlyoutLayer();
  const [mounted, setMounted] = useState(false);

  // For portal rendering
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const token = {};
    openFlyoutStack.push(token);
    const handleEsc = (e: KeyboardEvent) => {
      // Radix 的弹层(Dialog/AlertDialog/Popover/DropdownMenu/Select)在 capture 阶段
      // 处理 Esc 并 preventDefault,但不 stopPropagation —— 事件照样冒到 window。
      // 不查 defaultPrevented 的话,在 flyout 里按 Esc 关一个下拉,会连宿主 flyout
      // 一起关掉(嵌套两层 flyout 时两层一起没)。
      if (e.defaultPrevented) return;
      if (e.key !== 'Escape') return;
      // 不是栈顶就不动。关闭是异步的(setState),栈顶要等它的 effect 清理才出栈,
      // 所以同一次 Esc 里下面那层看到的栈顶仍是上层,不会跟着关。
      if (openFlyoutStack[openFlyoutStack.length - 1] !== token) return;
      handleClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
      const i = openFlyoutStack.indexOf(token);
      if (i !== -1) openFlyoutStack.splice(i, 1);
    };
  }, [isOpen, handleClose]);

  const getEntityUrl = useCallback(() => {
    if (!entityType || !entityId) return window.location.href;
    const baseUrl = window.location.origin;
    const mapping = ENTITY_ROUTE_MAP[entityType.toLowerCase()];
    if (mapping) {
      if (mapping.idParam) {
        const params = new URLSearchParams({ [mapping.idParam]: String(entityId) });
        return `${baseUrl}${mapping.route}?${params.toString()}`;
      }
      return `${baseUrl}${mapping.route}/${entityId}`;
    }
    return window.location.href;
  }, [entityType, entityId]);

  const handleShareClick = async () => {
    if (onShare) {
      onShare();
    } else {
      try {
        await navigator.clipboard.writeText(getEntityUrl());
        setShowCopyFeedback(true);
        setTimeout(() => setShowCopyFeedback(false), 2000);
      } catch (err) {
        console.error('Failed to copy!', err);
      }
    }
  };

  const sizeClasses: Record<string, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    full: 'max-w-full',
    custom: '' // No max-width class for custom size
  };

  const maxWidthClass = sizeClasses[size] || sizeClasses.xl;

  // Custom width style — maxWidth 用 min(px, 视口 − 留边)，大尺寸(550/650)在窄屏下也不溢出
  const customWidthStyle: React.CSSProperties = size === 'custom' && width
    ? {
        width: typeof width === 'number' ? `${width}px` : width,
        // 封顶到视口减去外层留边 --flyout-gutter（桌面 2.5rem、手机 0，见下面 positionClass），375 宽时不溢出
        maxWidth: typeof width === 'number'
          ? `min(${width}px, calc(100vw - var(--flyout-gutter, 2.5rem)))`
          : `min(${width}, calc(100vw - var(--flyout-gutter, 2.5rem)))`,
      }
    : {};

  const translateClass = side === 'right'
    ? (isOpen ? 'translate-x-0' : 'translate-x-full')
    : (isOpen ? 'translate-x-0' : '-translate-x-full');

  // 外层留边 --flyout-gutter：桌面 2.5rem（露出页面、点遮罩可关）；手机（< 768）为 0、面板占满全宽——
  // 390 宽时留边后面板只剩 350，标题被工具栏图标压住、内容一行放不下（2026-11-05 实测）。手机上用标题栏的 X 关。
  const gutterClass = '[--flyout-gutter:0px] md:[--flyout-gutter:2.5rem]';
  const positionClass = side === 'right'
    ? `right-0 inset-y-0 pl-[var(--flyout-gutter)] ${gutterClass}`
    : `left-0 inset-y-0 pr-[var(--flyout-gutter)] ${gutterClass}`;

  // Embedded mode: render content directly without fixed positioning
  if (embedded) {
    return (
      <div
        className={cn("h-full bg-background text-foreground flex flex-col", className)}
        style={floatingBallFootprint > 0
          ? ({ '--floating-ball-clearance': `${floatingBallFootprint}px` } as React.CSSProperties)
          : undefined}
      >
        {/* Header - conditionally rendered based on shouldShowHeader */}
        {shouldShowHeader && (
          <div className="flex items-center justify-between px-3 md:px-6 py-3 border-b border-border bg-background min-h-[56px] flex-shrink-0 z-20">
            <div className="flex-1 flex items-center gap-3 min-w-0" id={titleId}>
              <h2 className="text-base font-bold text-foreground truncate">{title}</h2>
            </div>

            {/* Action Icons */}
            <div className="flex shrink-0 items-center gap-0.5 md:gap-1 pl-1 md:pl-4">
              {/* Watch Button */}
              {!readonly && entityType && entityId && (
                <WatchButton
                  entityType={entityType}
                  entityId={entityId}
                  otherWatchersCount={otherWatchersCount}
                  showLabel={true}
                />
              )}

              {(entityType && entityId) && <div className="h-5 w-px bg-border mx-1" />}

              {actions}

              {/* Share Button with feedback */}
              {!readonly && (onShare || (entityType && entityId)) && (
                <div className="relative">
                  <button
                    onClick={handleShareClick}
                    className={cn(
                      "p-2 rounded-md transition-colors",
                      showCopyFeedback
                        ? "text-green-600 bg-green-50 dark:bg-green-950/40"
                        : "text-muted-foreground hover:text-sky-600 hover:bg-accent"
                    )}
                    title={t('ui.flyout.copyLink')}
                  >
                    {showCopyFeedback ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Share2 className="w-4 h-4" />
                    )}
                  </button>
                  {showCopyFeedback && (
                    <div className="absolute top-full right-0 mt-2 px-2 py-1 bg-foreground text-background text-[10px] font-bold rounded shadow-lg whitespace-nowrap z-50 animate-in fade-in zoom-in-95">
                      {t('ui.flyout.linkCopied')}
                    </div>
                  )}
                </div>
              )}

              {/* Open New Tab Button */}
              {(onOpenInNewTab || (entityType && entityId)) && (
                <button
                  className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                  title={t('ui.flyout.openInNewTab')}
                  onClick={onOpenInNewTab || (() => window.open(getEntityUrl(), '_blank'))}
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}

              {/* Divider - only show if there are other buttons before close */}
              {(actions || onOpenInNewTab || (entityType && entityId) || (!readonly && onShare)) && (
                <div className="h-5 w-px bg-border mx-1" />
              )}

              {/* Close Button */}
              <button
                type="button"
                className="p-2 rounded-md text-muted-foreground hover:text-muted-foreground hover:bg-accent transition-colors"
                onClick={handleClose}
                title={t('ui.dialog.close')}
              >
                <span className="sr-only">{t('ui.flyout.closePanel')}</span>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className={cn(
          "flex-1 bg-background",
          noPadding ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'
        )}>
          {children}
        </div>
      </div>
    );
  }

  const flyoutContent = (
    <div
      className={cn(
        // overflow-hidden：面板带 transform，里面的 fixed 子面板（如 Guest Guide 预览）以面板为定位基准；
        // 助手停靠、容器右缘让出了位置时，这些子面板收起后会滑进助手那一块，在容器右缘裁掉
        "fixed inset-0 z-50 overflow-hidden transition-all duration-300",
        isOpen ? 'visible' : 'invisible',
        // When no backdrop, allow clicks to pass through the outer container
        !showBackdrop && 'pointer-events-none',
        className,
        // 显式的层下限只往上抬：写死的 z-[60] 在助手里也抬到 z-[95]；普通页面不变
        liftedTo(className, flyoutLayer)
      )}
      // right：AI 助手 / Messages 侧边栏停靠时（--assistant-dock-inset / --chat-panel-dock-inset，同一时间只有一个非 0）
      // flyout 和遮罩都让出面板的宽度，面板不被盖住、不被压暗
      style={{ '--floating-ball-clearance': '1rem', right: 'calc(var(--assistant-dock-inset, 0px) + var(--chat-panel-dock-inset, 0px))' } as React.CSSProperties}
      aria-labelledby={shouldShowHeader ? titleId : undefined}
      role="dialog"
      aria-modal={showBackdrop ? "true" : "false"}
    >
      {/* Backdrop - conditionally rendered based on showBackdrop prop */}
      {showBackdrop && (
        <div
          className={cn(
            "absolute inset-0 bg-foreground/10 backdrop-blur-sm transition-opacity duration-300 ease-in-out pointer-events-auto", /* 有意半透明遮罩 */
            isOpen ? 'opacity-100' : 'opacity-0'
          )}
          onClick={handleClose}
        />
      )}

      <div className={cn("absolute flex max-w-full", positionClass)}>
        <div
          className={cn(
            // 宽度 = 视口减去外层留边 --flyout-gutter（桌面 2.5rem、手机 0），手机上不溢出右边。原来写 w-screen + max-w-[92vw]，
            // 但 cn（tailwind-merge）让后面的尺寸类 max-w-xl 等盖掉了 92vw，375 宽时面板比视口宽 40px、右边被裁。
            // 桌面上仍由尺寸类（max-w-xl …）封顶。
            "pointer-events-auto w-[calc(100vw-var(--flyout-gutter,2.5rem))] transform transition-transform duration-300 ease-in-out bg-background text-foreground shadow-2xl flex flex-col",
            maxWidthClass,
            translateClass
          )}
          style={customWidthStyle}
        >
          {/* Header - conditionally rendered based on shouldShowHeader */}
          {shouldShowHeader && (
            <div className="flex items-center justify-between px-3 md:px-6 py-3 border-b border-border bg-background min-h-[56px] flex-shrink-0 z-20">
              <div className="flex-1 flex items-center gap-3 min-w-0" id={titleId}>
                <h2 className="text-base font-bold text-foreground truncate">{title}</h2>
              </div>

              {/* Action Icons */}
              <div className="flex shrink-0 items-center gap-0.5 md:gap-1 pl-1 md:pl-4">
                {/* Watch Button */}
                {!readonly && entityType && entityId && (
                  <WatchButton
                    entityType={entityType}
                    entityId={entityId}
                    otherWatchersCount={otherWatchersCount}
                    showLabel={true}
                  />
                )}

                {!readonly && (entityType && entityId) && <div className="h-5 w-px bg-border mx-1" />}

                {actions}

                {/* Share Button with feedback */}
                {!readonly && (onShare || (entityType && entityId)) && (
                  <div className="relative">
                    <button
                      onClick={handleShareClick}
                      className={cn(
                        "p-2 rounded-md transition-colors",
                        showCopyFeedback
                          ? "text-green-600 bg-green-50 dark:bg-green-950/40"
                          : "text-muted-foreground hover:text-sky-600 hover:bg-accent"
                      )}
                      title={t('ui.flyout.copyLink')}
                    >
                      {showCopyFeedback ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Share2 className="w-4 h-4" />
                      )}
                    </button>
                    {showCopyFeedback && (
                      <div className="absolute top-full right-0 mt-2 px-2 py-1 bg-foreground text-background text-[10px] font-bold rounded shadow-lg whitespace-nowrap z-50 animate-in fade-in zoom-in-95">
                        {t('ui.flyout.linkCopied')}
                      </div>
                    )}
                  </div>
                )}

                {/* Open New Tab Button */}
                {(onOpenInNewTab || (entityType && entityId)) && (
                  <button
                    className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
                    title={t('ui.flyout.openInNewTab')}
                    onClick={onOpenInNewTab || (() => window.open(getEntityUrl(), '_blank'))}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                )}

                {/* Divider - only show if there are other buttons before close */}
                {(actions || onOpenInNewTab || (entityType && entityId) || (!readonly && onShare)) && (
                  <div className="h-5 w-px bg-border mx-1" />
                )}

                {/* Close Button */}
                <button
                  type="button"
                  className="p-2 rounded-md text-muted-foreground hover:text-muted-foreground hover:bg-accent transition-colors"
                  onClick={handleClose}
                  title={t('ui.dialog.close')}
                >
                  <span className="sr-only">{t('ui.flyout.closePanel')}</span>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          <div className={cn(
            "flex-1 bg-background",
            noPadding ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'
          )}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  // Use Portal if requested and mounted
  if (usePortal) {
    if (!mounted) return null;
    return createPortal(flyoutContent, document.body);
  }

  return flyoutContent;
};

// ============================================
// FlyoutHeader Component (Standalone Header)
// ============================================

interface FlyoutHeaderProps {
  title: React.ReactNode;
  onClose: () => void;
  entityType?: string;
  entityId?: string;
  /** Additional URL parameters for Open in New Tab / Share */
  urlParams?: Record<string, string>;
  /** Icons/buttons rendered at the far left of the header toolbar, before watch/open-new. */
  leadingActions?: React.ReactNode;
  actions?: React.ReactNode;
  showWatch?: boolean;
  showShare?: boolean;
  showOpenNew?: boolean;
  readonly?: boolean; // 只读模式，隐藏 Watch 按钮
  onRefresh?: () => void | Promise<void>;
  overflowActions?: ToolbarAction[];
}

export const FlyoutHeader: React.FC<FlyoutHeaderProps> = ({
  title,
  onClose,
  entityType,
  entityId,
  urlParams,
  leadingActions,
  actions,
  showWatch = true,
  showShare = true,
  showOpenNew = true,
  readonly = false,
  onRefresh,
  overflowActions,
}) => {
  const { t } = useTranslation('common')
  return (
    <div className="flex items-center justify-between px-3 md:px-6 py-3 border-b border-border bg-background min-h-[56px] flex-shrink-0 z-20">
      {/* Left: Title */}
      <div className="flex-1 flex items-center gap-3 min-w-0 overflow-visible">
        <div className="min-w-0 text-base font-bold text-foreground">{title}</div>
      </div>

      {/* Right: Actions */}
      <div className="flex shrink-0 items-center gap-0.5 md:gap-1 pl-1 md:pl-4">
        {leadingActions}
        <EntityToolbarButtons
          entityType={entityType}
          entityId={entityId}
          urlParams={urlParams}
          showWatch={showWatch}
          showShare={showShare}
          showOpenNew={showOpenNew}
          readonly={readonly}
          onRefresh={onRefresh}
          overflowActions={overflowActions}
        />

        {/* Custom Actions */}
        {actions}

        {/* Divider -- only render when toolbar or actions produced something */}
        {(actions || onRefresh || (overflowActions && overflowActions.length > 0) ||
          (entityType && entityId && (showShare || showOpenNew || (showWatch && !readonly)))) && (
          <div className="h-5 w-px bg-border mx-1" />
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          className="p-2 rounded-md text-muted-foreground hover:text-red-600 hover:bg-destructive/10 transition-colors"
          title={t('ui.dialog.close')}
          type="button"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Flyout 的标准内容区：内边距 + 独立滚动。
 *
 * **为什么要有这个组件**：`Flyout` 自己**不给内容区任何内边距**
 * （见上面 Content 那段：`noPadding` 时是 `flex flex-col overflow-hidden`，
 * 否则是 `overflow-y-auto`，两种都没有 padding）。于是每个调用方都得自己包一层
 * `flex-1 overflow-auto p-6` —— 2026-09-20 统计仓内有 18 处在手抄这个模式。
 * 抄对了没人注意，**漏抄的就是内容贴着 flyout 右边缘、被滚动条压住**，看起来
 * 像整块 UI 被裁掉（Registration Forms / Rental Agreement 两个模板面板就是
 * 这么坏的）。
 *
 * 用法：`<Flyout noPadding>` + `<FlyoutBody>` (+ 可选 `<FlyoutFooter>`)。
 * `noPadding` 是必须的 —— 它才会把内容区变成 flex 列容器，`flex-1` 与
 * `shrink-0` 的分配依赖它。
 *
 * 刻意**不改 `Flyout` 的默认行为**去自动加 padding：那 18 处已经自己包了，
 * 再加一层会变成双重内边距。存量各处改用本组件是独立的清理工作。
 */
export const FlyoutBody: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div className={cn('flex-1 overflow-y-auto px-6 py-5', className)}>
    {children}
  </div>
);

/**
 * Flyout 的标准底部操作栏：钉在底部、上边框分隔、不随正文滚动。
 *
 * 保存 / 取消这类动作跟在内容流末尾时，表单一长就得滚到底才能点到
 * （模板配置面板原来就是这样）。与 `FlyoutBody` 是一对，同样要求
 * `<Flyout noPadding>`。
 */
export const FlyoutFooter: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div
    className={cn(
      'flex shrink-0 items-center justify-between gap-2',
      'border-t border-border bg-background px-6 py-3',
      className,
    )}
  >
    {children}
  </div>
);
